import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../assets/icons/clock-v2-icon.svg';
// import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import endConsumerApi from '../../utils/api/end-consumer-v2';

import SidePaneModel from './SidePaneModel';
// import StationSidePanel from './StationSidePanel';

/**
 * Station media object
 */
interface StationMedia {
  url: string;
}

interface ConnectorStatusItem {
  status: string;
}

/**
 * Charging station details
 */
interface ChargingStationDetails {
  _id: string;
  name: string;
  address: string;
  latitude: string;
  longitude: string;
  opensAt: string;
  closesAt: string;
  isAvailable: boolean;
  connectorStatuses?: ConnectorStatusItem[];
  amenities: string[];
  medias: StationMedia[];
}

/**
 * Favorite station API response item
 */
interface FavoriteStation {
  _id: string;
  chargingStationDetails: ChargingStationDetails;
}

/**
 * Convert lat/lng strings to valid Google map position
 * @param {any} lat - The lat
 * @param {any} lng - The lng
 */
const getValidPosition = (lat?: string, lng?: string): google.maps.LatLngLiteral | null => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;

  return { lat: latitude, lng: longitude };
};

/**
 * Format time to local hh:mm format
 * @param {any} dateString - The dateString
 */
const formatTime = (dateString: string): string =>
  new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * Check if station is currently open
 * @param {string} [opensAt] - The opensAt time string
 * @param {string} [closesAt] - The closesAt time string
 * @param {boolean} [isAvailable] - Fallback availability flag
 * @returns {boolean} Whether station is open
 */
const isStationOpen = (opensAt?: string, closesAt?: string, isAvailable?: boolean): boolean => {
  if (opensAt && closesAt) {
    const now = new Date();
    const open = new Date(opensAt);
    const close = new Date(closesAt);

    if (!Number.isNaN(open.getTime()) && !Number.isNaN(close.getTime())) {
      if (close < open) return now >= open || now <= close;
      return now >= open && now <= close;
    }
  }

  return isAvailable ?? true;
};

/**
 * Generates an SVG data URL for a charging station map marker icon.
 * @param {string} color - The fill color of the marker pin.
 * @returns {string} The data URL string.
 */
const getStationMarkerIcon = (color: string): string => {
  const svg = `
    <svg width="44" height="45" viewBox="0 0 44 45"
      fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22 2.91602C14.4186 2.91602 8.25 8.81563 8.25 16.0645C8.25 19.5166 9.82352 24.1073 12.9267 29.7096C15.4189 34.2076 18.3021 38.275 19.8017 40.2988C20.0551 40.6446 20.3864 40.9259 20.7688 41.1197C21.1512 41.3136 21.5739 41.4146 22.0026 41.4146C22.4313 41.4146 22.854 41.3136 23.2363 41.1197C23.6187 40.9259 23.95 40.6446 24.2034 40.2988C25.7005 38.275 28.5863 34.2076 31.0784 29.7096C34.1765 24.1091 35.75 19.5183 35.75 16.0645C35.75 8.81563 29.5814 2.91602 22 2.91602Z"
        fill="${color}"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M23.2551 19.1954H28.9444C29.3221 19.1944 29.6668 19.386 29.8276 19.6883C29.9884 19.9907 29.9367 20.3475 29.6944 20.6041L19.3279 31.6541C19.1341 31.8446 18.8213 31.9 18.5603 31.7892C18.2993 31.6784 18.1517 31.4285 18.2002 31.1757L20.3058 21.7309H15.0504C14.7553 21.7289 14.4766 21.6094 14.2927 21.4052C14.1088 21.202 14.0394 20.9356 14.1033 20.6809L16.9226 9.63769C17.0184 9.24687 17.4083 8.96785 17.8609 8.96591H25.2198C25.5259 8.96202 25.8145 9.08938 25.9951 9.30715C26.1823 9.51812 26.2428 9.79616 26.1591 10.0538L23.2551 19.1954Z"
        fill="white"
      />
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * Resolves the color of the station marker based on connector status.
 * Priority: Available → Charging → Reserved → Red
 * @param {ConnectorStatusItem[]} [connectorStatuses] - Array of connector statuses.
 * @param {boolean} [isAvailable] - Fallback availability flag.
 * @returns {string} The hex color code.
 */
const getStationColor = (
  connectorStatuses?: ConnectorStatusItem[],
  isAvailable?: boolean,
): string => {
  const statuses = connectorStatuses || [];

  // 🟢 GREEN: If ANY connector is Available
  const hasAvailable = statuses.some((item) => item.status === 'Available');

  if (hasAvailable || (statuses.length === 0 && isAvailable)) {
    return '#22c55e';
  }

  // 🔵 BLUE: If ANY connector is Charging / Preparing / Finishing
  const hasCharging = statuses.some(
    (item) =>
      item.status === 'Charging' ||
      item.status === 'Preparing' ||
      item.status === 'Finishing',
  );

  if (hasCharging) {
    return '#3b82f6';
  }

  // 🟡 YELLOW: If ANY connector is Reserved
  const hasReserved = statuses.some((item) => item.status === 'Reserved');

  if (hasReserved) {
    return '#FFC107';
  }

  // 🔴 RED: No Available, Charging, Preparing, Finishing, or Reserved
  return '#e41010';
};

interface ModelDetailsInterface {
  title?: string;
  content: JSX.Element;
  url?: string;
}

/**
 * Map side panel showing favorite charging stations
 */
function MapSidePanel(): JSX.Element {
  const { t } = useTranslation('sidePanel');
  const { isLoaded } = useGoogleMaps();
  const dispatch = useAppDispatch();
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);
  const navigate = useNavigate();
  const location = useLocation();

  const [stations, setStations] = useState<FavoriteStation[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    content: <div />,
    url: '',
  });

  // /**
  //  * Handle open station details
  //  * @param {any} station - The station
  //  */
  // const handleOpenStationDetails = (station: ChargingStationDetails) => {
  //   navigate(`?stationId=${station._id}`);
  //   setIsOpen(true);
  //   setModelDetails({
  //     title: '',
  //     content: <StationSidePanel fetchIdFrom="queryParams" />,
  //     url: `/ev-admin/station-management/${station._id}/station-dashboard?searchBy=${station.name}&source=${location.pathname}`,
  //   });
  // };

  /**
   * Open Station NewTab
   * @param {any} station - The station
   */
  const handleOpenStationNewTab = (station: ChargingStationDetails) => {
    navigate(
      `/ev-admin/station-management/${station._id}/station-dashboard?searchBy=${station.name}`,
      { state: { source: location.pathname } },
    );
  };

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
  };

  /**
   * Fetch favorite stations
   */
  const fetchStations = useCallback(
    async (userCustomId: string) => {
      try {
        const response = await endConsumerApi.fetchFavoriteStations(userCustomId);
        const rawResults = response?.data?.results || [];
        const mappedStations: FavoriteStation[] = rawResults.map((item: any) => ({
          _id: item.locationId || item.id || item._id || '',
          chargingStationDetails: {
            _id: item.locationId || item.id || item._id || '',
            name: item.name || '',
            address: item.address || item.city || '',
            latitude: item.latitude || '0',
            longitude: item.longitude || '0',
            opensAt: item.opensAt || '',
            closesAt: item.closesAt || '',
            isAvailable: item.found ?? item.isAvailable ?? true,
            connectorStatuses: item.connectorStatuses || item.connectors || [],
            amenities: item.amenities || [],
            medias: item.medias || [],
          },
        }));
        setStations(mappedStations);
      } catch (error: unknown) {
        const message =
          error && typeof error === 'object' && 'response' in error
            ? (error as any)?.response?.data?.message
            : 'Something went wrong!';

        dispatch(
          setNotificationMessage({
            status: true,
            message,
            type: 'error',
            code: 0,
          }),
        );
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId) {
      fetchStations(selectedEndConsumer.userCustomId);
    }
  }, [fetchStations, selectedEndConsumer]);

  /**
   * Static map options (memoized once)
   */
  const miniMapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      clickableIcons: false,
      draggable: false,
      zoomControl: false,
      scrollwheel: false,
    }),
    [],
  );

  return (
    <div className="map-side-panel-wrap">
      <SidePaneModel
        open={isOpen}
        title={modelDetails.title}
        content={modelDetails.content}
        closePanel={handleCloseModal}
        url={modelDetails.url}
      />
      <div className="map-side-panel-details">
        {stations.map((item) => {
          const station = item.chargingStationDetails;
          const open = isStationOpen(station.opensAt, station.closesAt, station.isAvailable);
          const mapCenter = getValidPosition(station.latitude, station.longitude);

          return (
            <div key={station._id} className="each-map-data">
              {/* MINI MAP */}
              {isLoaded && mapCenter && (
                <div className="mini-map">
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter}
                    zoom={12}
                    options={miniMapOptions}>
                    <Marker
                      position={mapCenter}
                      icon={{
                        url: getStationMarkerIcon(
                          getStationColor(station.connectorStatuses, station.isAvailable),
                        ),
                        scaledSize: new window.google.maps.Size(32, 32),
                      }}
                    />
                  </GoogleMap>
                </div>
              )}

              <p
                className="hyperlink"
                role="presentation"
                // onClick={() => handleOpenStationDetails(station)}
                >
                {station.name}
              </p>
              <p className="address">{station.address}</p>
              <p className="address">{station.address}</p>

              <div className="map-time-content">
                <div className="map-time">
                  <p className={open ? 'open' : 'closed'}>{open ? t('open') : t('closed')}</p>
                  {station.opensAt && station.closesAt && (
                    <p className="icon">
                      <ClockIcon /> {formatTime(station.opensAt)} - {formatTime(station.closesAt)}
                    </p>
                  )}
                </div>
                <div
                  className="hyperlink"
                  role="presentation"
                  onClick={() => handleOpenStationNewTab(station)}>
                  {/* <NewTabIcon />{' '} */}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MapSidePanel;
